import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Formik, Form, getIn, FormikProps, setIn } from 'formik';
import * as _ from 'lodash';
import React, { useRef, useState } from 'react';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { colors } from 'packages/common-frontend/colors';
import {
  CheckboxFormField,
  InputFormField,
  TextareaFormField,
} from 'packages/common-frontend/components/FormField';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import { errorToastAdminConfig } from 'packages/common-frontend/constants';
import {
  CreditAgreement,
  formatBasePropertyKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FiInfo } from 'react-icons/fi';

export type EditPropertyModalOnSave = (
  props: any,
) => CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

type EditPropertyDeleteConfirmationAlertProps = {
  alertHeaderLabel: string;
  alertBodyLabel: string;
  confirmButtonLabel: string;
  conditionalPropertiesToggleLabel: string;
  onConfirm: () => void;
  isDisabled?: boolean;
};
const EditPropertyDeleteConfirmationAlert = ({
  alertHeaderLabel,
  alertBodyLabel,
  confirmButtonLabel,
  conditionalPropertiesToggleLabel,
  onConfirm,
  isDisabled = false,
}: EditPropertyDeleteConfirmationAlertProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  return (
    <>
      <Flex mt="24px">
        <Spacer />
        <Tooltip
          label={
            isDisabled
              ? 'This configuration has already been committed and cannot be deleted.'
              : ''
          }
          maxW="300px"
        >
          {/* tooltip ref location cannot be determined when directly wrapping a functional component, so adding a span wrapper */}
          <span>
            <Button
              onClick={onOpen}
              variant="ghost"
              padding="0"
              isDisabled={isDisabled}
              p="16px"
            >
              <Flex alignItems="center">
                <FinleyIcon icon="trash-can" />
                <Text ml="8px" fontWeight={500}>
                  Delete {conditionalPropertiesToggleLabel}
                </Text>
              </Flex>
            </Button>
          </span>
        </Tooltip>
      </Flex>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {alertHeaderLabel}
            </AlertDialogHeader>

            <AlertDialogBody>{alertBodyLabel}</AlertDialogBody>

            <AlertDialogFooter>
              <Button
                variant="outline"
                colorScheme="aurora"
                borderWidth="2px"
                onClick={onClose}
                mr="32px"
              >
                Cancel
              </Button>
              <Button colorScheme="aurora" color="white" onClick={onConfirm}>
                {confirmButtonLabel}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

type TopPropertiesProps = {
  localFormProps: FormikProps<any>;
  collapsible: boolean;
  children:
    | ((localFormProps: FormikProps<any>) => React.ReactElement)
    | undefined;
  isClientWipMode: boolean;
};
const TopProperties = ({
  localFormProps,
  children,
  isClientWipMode,
  collapsible,
}: TopPropertiesProps) => {
  const content = (
    <VStack spacing={5}>
      <InputFormField
        propName="name"
        label="Name"
        formProps={localFormProps}
        value={localFormProps.values.name}
        placeholder="Enter name"
      />
      {children && children(localFormProps)}
      <InputFormField
        propName="section"
        label="Section"
        formProps={localFormProps}
        value={localFormProps.values.section}
        placeholder="Enter section"
      />
      <TextareaFormField
        propName="description"
        label="Description"
        formProps={localFormProps}
        value={localFormProps.values.description}
        placeholder="Enter description"
      />
      <TextareaFormField
        propName="_note_"
        label="Note"
        formProps={localFormProps}
        value={localFormProps.values._note_}
        placeholder="Enter note"
        inputHeight="30"
      />
      {!isClientWipMode && (
        <CheckboxFormField
          propName="_isWrittenIn_"
          label="Is Written In"
          formProps={localFormProps}
          value={localFormProps.values._isWrittenIn_}
        />
      )}
    </VStack>
  );

  return collapsible ? (
    <Accordion allowToggle>
      <AccordionItem>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            Basic Information
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4}>{content}</AccordionPanel>
      </AccordionItem>
    </Accordion>
  ) : (
    <>{content}</>
  );
};

export const EditPropertyModal = <DataT,>({
  basePropertyKey,
  children,
  configChildren,
  isOpen,
  setIsOpen,
  wipConfig,
  onSave,
  modalHeaderLabel,
  conditionalProperties,
}: {
  basePropertyKey: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  children?: (localFormProps: FormikProps<any>) => React.ReactElement;
  configChildren?: (localFormProps: FormikProps<any>) => React.ReactElement;
  onSave?: EditPropertyModalOnSave;
  modalHeaderLabel?: string;

  conditionalProperties?: {
    conditionalPropertiesToggleLabel: string;
    conditionalPropertiesComponent: (
      localFormProps: FormikProps<any>,
      validationErrors: Array<string>,
    ) => React.ReactElement; // conditionalPropertiesComponent will render when a toggle is triggered. the toggle label can be specified.
    conditionalPropertiesValidation: (values: DataT) => Array<string>; // additional validation from the conditional properties components to be included as part of the main property validation
    conditionalPropertiesOnSubmitTransform?: (values: DataT) => any;
    useConditionalProperties: boolean; // whether to display conditional properties section
    onDeleteConditionalProperties?: (localFormProps: FormikProps<any>) => void; // executes when deleting the set of conditional properties
    onAddConditionalProperties?: (localFormProps: FormikProps<any>) => void; // executes when adding a new set of conditional properties
    getIsPropertyKeyCurrentlyCommitted?: (
      localFormProps: FormikProps<any>,
    ) => boolean; // these are property keys for this config section that already exist in the latest committed credit agreement for the facility
  };
}) => {
  const toast = useToast();
  const {
    formProps,
    saveCreditAgreementFullArtifactWip,
    operationMode,
  } = wipConfig;

  const [useConditionalProperties, setUseConditionalProperties] = useState(
    conditionalProperties?.useConditionalProperties ?? false,
  );
  const [
    conditionalPropertiesValidationErrors,
    setConditionalPropertiesValidationErrors,
  ] = useState<Array<string>>([]);

  const currentProperty = getIn(formProps.values, basePropertyKey);

  const onClose = () => {
    setIsOpen(false);
    setUseConditionalProperties(
      conditionalProperties?.useConditionalProperties ?? false,
    );
    setConditionalPropertiesValidationErrors([]);
  };

  const isClientWipMode = operationMode === 'client';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>
          {modalHeaderLabel ?? formatBasePropertyKey(basePropertyKey)}
        </ModalHeader>
        <ModalCloseButton m="24px" />
        <ModalBody>
          <Formik
            onSubmit={(props) => {
              let wipCreditAgreement = formProps.values;
              let submitProps = { ...props };
              let conditionalPropertiesErrors: string[] = [];

              if (conditionalProperties) {
                conditionalPropertiesErrors = conditionalProperties.conditionalPropertiesValidation(
                  props,
                );

                setConditionalPropertiesValidationErrors(
                  (previousValidationErrors) => {
                    if (
                      !_.isEqual(
                        conditionalPropertiesErrors,
                        previousValidationErrors,
                      )
                    ) {
                      return conditionalPropertiesErrors;
                    }
                    return previousValidationErrors;
                  },
                );
              }

              try {
                if (onSave) wipCreditAgreement = onSave(props);
              } catch (err: any) {
                errorToastAdminConfig.title = err.message
                  ? err.message
                  : 'Cannot save property with errors';
                toast(errorToastAdminConfig);
                return;
              }

              if (conditionalPropertiesErrors.length === 0) {
                if (
                  conditionalProperties?.conditionalPropertiesOnSubmitTransform
                ) {
                  submitProps = conditionalProperties.conditionalPropertiesOnSubmitTransform(
                    submitProps,
                  );
                }
                // set to change setFieldValue key
                formProps.setFieldValue(basePropertyKey, {
                  ...submitProps,
                });

                const updatedWipCreditAgreement = setIn(
                  wipCreditAgreement,
                  basePropertyKey,
                  props,
                );

                saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: formProps.values.id,
                      creditFacilityId: formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(
                        updatedWipCreditAgreement,
                      ),
                    },
                  },
                });
                setIsOpen(false);
              }
            }}
            initialValues={{
              ...currentProperty,
            }}
          >
            {(localFormProps) => (
              <Form onSubmit={localFormProps.handleSubmit}>
                <TopProperties
                  localFormProps={localFormProps}
                  isClientWipMode={isClientWipMode}
                  collapsible={false}
                >
                  {children}
                </TopProperties>
                <VStack spacing={5} mt="10px">
                  {configChildren && (
                    <Flex width="100%" alignItems="center">
                      <Text textAlign="left" fontWeight="bold" mr="5px">
                        Config
                      </Text>
                      <Tooltip
                        hasArrow
                        placement="right"
                        label="Configs are not displayed to the user and are only used for internal processes"
                        flexGrow={1}
                      >
                        <Icon>
                          <FiInfo size="24px" />
                        </Icon>
                      </Tooltip>
                    </Flex>
                  )}
                  {configChildren && configChildren(localFormProps)}
                  {(() => {
                    if (conditionalProperties) {
                      const {
                        conditionalPropertiesToggleLabel,
                        conditionalPropertiesComponent,
                        onDeleteConditionalProperties,
                        onAddConditionalProperties,
                        getIsPropertyKeyCurrentlyCommitted,
                      } = conditionalProperties;

                      const isDeleteEnabled = !!onDeleteConditionalProperties;
                      const isPropertyKeyCurrentlyCommitted =
                        getIsPropertyKeyCurrentlyCommitted &&
                        getIsPropertyKeyCurrentlyCommitted(localFormProps);

                      return (
                        <>
                          <FormLabel w="100%">
                            Calculation Configuration
                          </FormLabel>
                          <Box
                            border="1px"
                            borderColor={colors.gray[200]}
                            borderRadius="8px"
                            p="24px"
                            w="100%"
                          >
                            {useConditionalProperties ? (
                              <>
                                {conditionalPropertiesComponent(
                                  localFormProps,
                                  conditionalPropertiesValidationErrors,
                                )}
                                {isDeleteEnabled && (
                                  <EditPropertyDeleteConfirmationAlert
                                    alertHeaderLabel="Are you sure?"
                                    alertBodyLabel="Your configuration will be deleted."
                                    confirmButtonLabel="Yes, Delete Configuration"
                                    onConfirm={() => {
                                      onDeleteConditionalProperties(
                                        localFormProps,
                                      );
                                      setUseConditionalProperties(
                                        !useConditionalProperties,
                                      );
                                    }}
                                    conditionalPropertiesToggleLabel={
                                      conditionalPropertiesToggleLabel
                                    }
                                    isDisabled={
                                      isClientWipMode &&
                                      isPropertyKeyCurrentlyCommitted
                                    } // ir propertyKey currently exists in the latest committed CA, disable ability to delete the config for lenders
                                  />
                                )}
                              </>
                            ) : (
                              <Flex>
                                <Spacer />
                                <Flex
                                  alignItems="center"
                                  cursor="pointer"
                                  onClick={() => {
                                    if (onAddConditionalProperties) {
                                      onAddConditionalProperties(
                                        localFormProps,
                                      );
                                      setUseConditionalProperties(
                                        !useConditionalProperties,
                                      );
                                    }
                                  }}
                                >
                                  <FinleyIcon icon="plus" />
                                  <Text ml="8px" fontWeight={500}>
                                    Add {conditionalPropertiesToggleLabel}
                                  </Text>
                                </Flex>
                              </Flex>
                            )}
                          </Box>
                        </>
                      );
                    }
                    return null;
                  })()}
                </VStack>
                <Divider mt="32px" mb="32px" />
                <Flex>
                  <Spacer />
                  <Button
                    aria-label="Close"
                    onClick={onClose}
                    colorScheme="aurora"
                    variant="outline"
                    borderWidth="2px"
                    mr="24px"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    aria-label="Save"
                    isDisabled={_.keys(formProps.errors).length > 0}
                    colorScheme="aurora"
                    color="white"
                  >
                    Save
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
